import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Container, Typography, Stepper, Step, StepLabel, Grid, Chip, Button,
  TextField, RadioGroup, FormControlLabel, Radio, Alert, Divider,
  CircularProgress, Paper, Stack, Avatar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_DOCTOR_APPOINTMENT_URL;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
});
const STEPS = ["Doctor", "Date", "Time", "Reason", "Confirm"];
const TEAL = "#006D77";

const BookDoctorAppointment = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [dates, setDates] = useState([]);          // available "YYYY-MM-DD"
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState(null);
  const [notes, setNotes] = useState("");
  const [patientName, setPatientName] = useState("");
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/booking/doctors`, authHeaders())
      .then((r) => setDoctors(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/booking/consult-reasons`, authHeaders())
      .then((r) => setReasons(r.data || [])).catch(() => {});
  }, []);

  const pickDoctor = async (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedDate(null);
    setSelectedSlot(null);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/booking/doctors/${doctor.id}/available-dates`, authHeaders());
      setDates((res.data || []).map((d) => String(d).split("T")[0]));
      setStep(1);
    } catch {
      setError("Could not load available dates. Please try again.");
    }
  };



  const pickDate = async (dayjsDate) => {
  const iso = dayjsDate.format("YYYY-MM-DD");
  if (!dates.includes(iso)) return;
  setSelectedDate(iso);
  setSelectedSlot(null);
  setError("");
  try {
    const res = await axios.get(`${API_BASE}/booking/doctors/${selectedDoctor.id}/slots`, {
      ...authHeaders(), params: { date: iso },
    });
    let list = res.data || [];

    // if the chosen date is today, drop slots whose start time has already passed
    const isToday = dayjs().format("YYYY-MM-DD") === iso;
    if (isToday) {
      const now = dayjs();
      list = list.filter((s) => {
        const [h, m] = (s.start_time || "00:00").split(":").map(Number);
        const slotTime = dayjs().hour(h).minute(m).second(0);
        return slotTime.isAfter(now);
      });
    }

    setSlots(list);
    setStep(2);
  } catch {
    setError("Could not load slots for this date. Please try again.");
  }
};

  const pickSlot = (slot) => { setSelectedSlot(slot); setStep(3); };

  const submitBooking = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE}/booking/doctor-appointments`, {
        doctor_id: selectedDoctor.id, slot_id: selectedSlot.id,
        reason_id: selectedReasonId, patient_name: patientName || "Self",
        is_self: true, notes,
      }, authHeaders());
      setConfirmed(res.data);
    } catch (e) {
      if (e.response?.status === 409) {
        setError("That slot was just booked by someone else. Please pick another time.");
        try {
          const res = await axios.get(`${API_BASE}/booking/doctors/${selectedDoctor.id}/slots`, {
            ...authHeaders(), params: { date: selectedDate },
          });
          setSlots(res.data || []);
        } catch {}
        setSelectedSlot(null);
        setStep(2);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  if (confirmed) return <ConfirmationScreen appointment={confirmed} />;

  // calendar day renderer: only available dates are enabled + highlighted
  const AvailableDay = (props) => {
    const { day, outsideCurrentMonth, ...rest } = props;
    const iso = day.format("YYYY-MM-DD");
    const available = dates.includes(iso);
    const isSel = selectedDate === iso;
    return (
      <PickersDay {...rest} day={day} outsideCurrentMonth={outsideCurrentMonth}
        disabled={!available}
        sx={{
          ...(available && { bgcolor: "#E0F7F8", color: TEAL, fontWeight: 700,
            "&:hover": { bgcolor: "#B2E0E4" } }),
          ...(isSel && { bgcolor: TEAL, color: "#fff", "&:hover": { bgcolor: "#005662" } }),
        }} />
    );
  };

  // group doctors by specialization
  const grouped = doctors.reduce((acc, d) => {
    const key = d.specialization?.trim() || "General";
    (acc[key] = acc[key] || []).push(d);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={step > 0 ? back : () => navigate(-1)}
        sx={{ mb: 2, textTransform: "none", color: TEAL, fontWeight: 600 }}>
        Back
      </Button>

      <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom
        sx={{ fontSize: { xs: "1.7rem", sm: "2.125rem" } }}>
        Book a Doctor Consultation
      </Typography>
      <Typography color="text.secondary" mb={4}>
        Simple steps · Better care · All in one place
      </Typography>

      <Stepper activeStep={step} alternativeLabel
        sx={{ mb: 5, "& .MuiStepLabel-label": { fontSize: { xs: 11, sm: 14 } } }}>
        {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}


        {/* Step 0: Doctor */}
        {step === 0 && (
        <Box>
            {doctors.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: "center", bgcolor: "#F8FAFC" }}>
                <Typography variant="h6" fontWeight={700}>No doctors available</Typography>
                <Typography color="text.secondary">Please check back soon.</Typography>
            </Paper>
            ) : (
            <Grid container spacing={3}>
                {doctors.map((doc) => (
                <Grid item xs={12} sm={6} key={doc.id}>
                    <Paper
                    elevation={0}
                    sx={{
                        display: "flex",
                        height: "100%",
                        minHeight: 200,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #E2E8F0",
                        transition: "all 0.25s ease",
                        "&:hover": {
                        boxShadow: "0 14px 34px rgba(0,109,119,0.16)",
                        borderColor: TEAL,
                        transform: "translateY(-4px)",
                        },
                    }}
                    >
                    {/* Photo */}
                    <Box sx={{ width: 150, flexShrink: 0, bgcolor: "#E0F2F1" }}>
                        {doc.photo_url ? (
                        <Box component="img" src={doc.photo_url} alt={doc.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        ) : (
                        <Box sx={{
                            width: "100%", height: "100%", minHeight: 200,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: TEAL, color: "#fff", fontSize: 52, fontWeight: 800,
                        }}>
                            {doc.name?.[0]?.toUpperCase()}
                        </Box>
                        )}
                    </Box>

                    {/* Details */}
                    <Box sx={{ flex: 1, minWidth: 0, p: 2.5, display: "flex", flexDirection: "column" }}>
                        <Typography fontWeight={800} fontSize={17} color="#0F2A3F"
                        sx={{ textTransform: "uppercase", lineHeight: 1.3 }} noWrap>
                        Dr. {doc.name}
                        </Typography>

                        {doc.specialization && (
                        <Typography variant="caption" fontWeight={700} color={TEAL}
                            sx={{ mt: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }} noWrap>
                            {doc.specialization}
                        </Typography>
                        )}

                        {doc.qualification && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} noWrap>
                            {doc.qualification}
                        </Typography>
                        )}

                        <Stack direction="row" spacing={1} mt={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`₹${doc.fee}`}
                            sx={{ bgcolor: "#E0F7F8", color: TEAL, fontWeight: 700 }} />
                        </Stack>

                        {doc.location && (
                        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.75} sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ flexShrink: 0 }}>📍</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{doc.location}</Typography>
                        </Stack>
                        )}

                        <Button
                        variant="contained"
                        onClick={() => pickDoctor(doc)}
                        sx={{
                            mt: "auto", bgcolor: TEAL, textTransform: "none",
                            fontWeight: 700, borderRadius: 1.5, alignSelf: "flex-start", px: 2.5,
                            "&:hover": { bgcolor: "#005662" },
                        }}
                        >
                        Book Appointment
                        </Button>
                    </Box>
                    </Paper>
                </Grid>
                ))}
            </Grid>
            )}
        </Box>
        )}
      {/* Step 1: Date (calendar) */}
      {step === 1 && (
        <Box>
          <Typography variant="h6" fontWeight={700} mb={1}>
            Choose a date for Dr. {selectedDoctor?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Highlighted dates have open slots.
          </Typography>
          {dates.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: "center", bgcolor: "#F8FAFC" }}>
              <EventBusyIcon sx={{ fontSize: 56, color: "#94A3B8", mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>No open dates yet</Typography>
              <Typography color="text.secondary">This doctor has no availability. Please check back soon.</Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3, display: "inline-block", bgcolor: "#fff" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar disablePast value={selectedDate ? dayjs(selectedDate) : null}
                  onChange={pickDate} slots={{ day: AvailableDay }} />
              </LocalizationProvider>
            </Paper>
          )}
        </Box>
      )}

      {/* Step 2: Time */}
      {step === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Available slots on {new Date(selectedDate).toLocaleDateString("en-IN", { day: "numeric", month: "long" })}
          </Typography>
          {slots.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: "center", bgcolor: "#F8FAFC" }}>
              <EventBusyIcon sx={{ fontSize: 56, color: "#94A3B8", mb: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>No slots available</Typography>
              <Typography color="text.secondary" mb={3}>
                There are no open time slots for this date. Please choose another date.
              </Typography>
              <Button variant="contained" sx={{ bgcolor: TEAL, textTransform: "none" }} onClick={() => setStep(1)}>
                Choose another date
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={1.5}>
              {slots.map((slot) => (
                <Grid item xs={6} sm={4} md={3} key={slot.id}>
                  <Button fullWidth
                    variant={selectedSlot?.id === slot.id ? "contained" : "outlined"}
                    onClick={() => pickSlot(slot)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600,
                      ...(selectedSlot?.id === slot.id && { bgcolor: TEAL }) }}>
                    {slot.start_time} - {slot.end_time}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Step 3: Reason */}
      {step === 3 && (
        <Box>
          <Typography variant="h6" fontWeight={700} mb={2}>What would you like to consult about?</Typography>
          <RadioGroup value={selectedReasonId || ""} onChange={(e) => setSelectedReasonId(e.target.value)}>
            {reasons.map((r) => (
              <FormControlLabel key={r.id} value={r.id} control={<Radio />} label={r.label} />
            ))}
          </RadioGroup>
          <TextField fullWidth multiline rows={3} margin="normal"
            label="Describe your concern (optional)"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          <TextField fullWidth margin="normal" label="Patient name"
            value={patientName} onChange={(e) => setPatientName(e.target.value)} />
          <Button variant="contained" sx={{ mt: 2, bgcolor: TEAL, borderRadius: 2, textTransform: "none" }}
            disabled={!selectedReasonId || !patientName} onClick={() => setStep(4)}>
            Continue
          </Button>
        </Box>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, maxWidth: 560 }}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar src={selectedDoctor?.photo_url} sx={{ bgcolor: TEAL, width: 56, height: 56 }}>
              {selectedDoctor?.name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Confirm Appointment</Typography>
              <Typography variant="body2" color="text.secondary">Dr. {selectedDoctor?.name}</Typography>
            </Box>
          </Stack>
          <Row label="Patient" value={patientName} />
          <Row label="Date" value={new Date(selectedDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} />
          <Row label="Time" value={`${selectedSlot?.start_time} - ${selectedSlot?.end_time}`} />
          <Row label="Location" value={selectedDoctor?.location || "Ayuja Life Care Services"} />
          <Divider sx={{ my: 2 }} />
          <Row label="Consultation Fee" value={`₹${selectedDoctor?.fee}`} bold />
          <Button fullWidth variant="contained" sx={{ mt: 3, bgcolor: TEAL, borderRadius: 2, py: 1.3, textTransform: "none" }}
            onClick={submitBooking} disabled={loading}>
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Confirm & Book"}
          </Button>
        </Paper>
      )}
    </Container>
  );
};

const Row = ({ label, value, bold }) => (
  <Box display="flex" justifyContent="space-between" py={0.75}>
    <Typography color="text.secondary">{label}</Typography>
    <Typography fontWeight={bold ? 800 : 600}>{value}</Typography>
  </Box>
);

const ConfirmationScreen = ({ appointment }) => (
  <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
    <CheckCircleIcon sx={{ fontSize: 72, color: "#2e7d32" }} />
    <Typography variant="h5" fontWeight={800} mt={1}>Appointment Confirmed!</Typography>
    <Typography color="text.secondary" mb={3}>Your consultation is booked successfully.</Typography>
    <Typography color="text.secondary" mb={3}>Upload Prescription if you have</Typography>
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "left" }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Avatar src={appointment.doctor?.photo_url} sx={{ bgcolor: TEAL, width: 56, height: 56 }}>
          {appointment.doctor?.name?.[0]}
        </Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">Appointment ID</Typography>
          <Typography variant="h6" fontWeight={800} color={TEAL} fontFamily="monospace">
            {appointment.appointment_code}
          </Typography>
        </Box>
      </Stack>
      <Row label="Doctor" value={appointment.doctor?.name} />
      <Row label="Date & Time" value={`${appointment.slot?.date} · ${appointment.slot?.start_time}`} />
      <Row label="Location" value={appointment.doctor?.location || "Ayuja Life Care Services"} />
    </Paper>
  </Container>
);

export default BookDoctorAppointment;